// CSV import utilities for user bulk creation
// Handles username generation, validation, CSV parsing with auto-detection of separators
// Support for 5 site columns (site1 to site5) and automatic site column ignoring for admin/director roles
// CHANGE: Updated to use createCSVBlob for proper UTF-8 BOM handling
// CHANGE: Removed all debug console statements for production

import { supabase } from './supabase';
import type { Site } from './supabase';
import { createCSVBlob } from './csvEncoding';

export interface CSVUserRow {
  lineNumber: number;
  first_name: string;
  last_name: string;
  role: string;
  site1: string;
  site2: string;
  site3: string;
  site4: string;
  site5: string;
  generatedUsername: string;
  validationErrors: string[];
  isValid: boolean;
}

export interface ParsedCSVResult {
  users: CSVUserRow[];
  newSites: string[];
  errors: string[];
}

// Remove accents and special characters from a string
export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, '') // Remove non-alphanumeric characters
    .trim();
}

// Generate username according to the rule: lastname + first 2 letters of firstname
export function generateUsername(lastName: string, firstName: string): string {
  const normalizedLastName = normalizeString(lastName);
  const normalizedFirstName = normalizeString(firstName);

  // Get first 2 letters of first name (or less if shorter)
  const firstNamePrefix = normalizedFirstName.substring(0, 2);

  return normalizedLastName + firstNamePrefix;
}

// Check if usernames are available in the database
export async function checkUsernamesAvailability(usernames: string[]): Promise<Set<string>> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('username')
      .in('username', usernames);

    if (error) {
      return new Set();
    }

    return new Set(data?.map(u => u.username) || []);
  } catch (error) {
    return new Set();
  }
}

// Detect CSV separator automatically (comma, semicolon, or tab)
export function detectCSVSeparator(csvContent: string): string {
  const firstLine = csvContent.split(/\r?\n/)[0];

  // Count occurrences of each potential separator
  const separators = [',', ';', '\t'];
  const counts = separators.map(sep => ({
    separator: sep,
    count: (firstLine.match(new RegExp(`\\${sep}`, 'g')) || []).length
  }));

  // Sort by count descending and return the most frequent one
  counts.sort((a, b) => b.count - a.count);

  // Return comma as default if no separator found
  return counts[0].count > 0 ? counts[0].separator : ',';
}

// Validate role value
export function isValidRole(role: string): boolean {
  const validRoles = ['operator', 'manager', 'director', 'admin'];
  return validRoles.includes(role.toLowerCase());
}

// Parse CSV content and validate data
export async function parseCSV(
  csvContent: string,
  existingSites: Site[]
): Promise<ParsedCSVResult> {
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  const users: CSVUserRow[] = [];
  const errors: string[] = [];
  const allNewSites = new Set<string>();

  // Check if CSV has content
  if (lines.length === 0) {
    errors.push('Le fichier CSV est vide');
    return { users: [], newSites: [], errors };
  }

  // Detect CSV separator automatically
  const separator = detectCSVSeparator(csvContent);

  // Parse header
  const headerLine = lines[0];
  const headers = headerLine.split(separator).map(h => h.trim().toLowerCase());

  // Validate required columns
  const requiredColumns = ['first_name', 'last_name', 'role'];
  const missingColumns = requiredColumns.filter(col => !headers.includes(col));

  if (missingColumns.length > 0) {
    errors.push(`Colonnes manquantes: ${missingColumns.join(', ')}`);
    return { users: [], newSites: [], errors };
  }

  // Get column indices
  const firstNameIndex = headers.indexOf('first_name');
  const lastNameIndex = headers.indexOf('last_name');
  const roleIndex = headers.indexOf('role');
  const site1Index = headers.indexOf('site1');
  const site2Index = headers.indexOf('site2');
  const site3Index = headers.indexOf('site3');
  const site4Index = headers.indexOf('site4');
  const site5Index = headers.indexOf('site5');

  // Parse data rows
  const existingSiteNames = new Set(existingSites.map(s => s.name.toLowerCase()));
  const generatedUsernames = new Map<string, number>(); // Track username occurrences

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#')) continue; // Skip empty lines and comments

    const lineNumber = i + 1;
    const values = parseCSVLine(line, separator);

    if (values.length < requiredColumns.length) {
      errors.push(`Ligne ${lineNumber}: nombre de colonnes insuffisant`);
      continue;
    }

    const firstName = values[firstNameIndex]?.trim() || '';
    const lastName = values[lastNameIndex]?.trim() || '';
    const role = values[roleIndex]?.trim().toLowerCase() || '';

    // Get site values from columns
    const site1 = site1Index >= 0 ? (values[site1Index]?.trim() || '') : '';
    const site2 = site2Index >= 0 ? (values[site2Index]?.trim() || '') : '';
    const site3 = site3Index >= 0 ? (values[site3Index]?.trim() || '') : '';
    const site4 = site4Index >= 0 ? (values[site4Index]?.trim() || '') : '';
    const site5 = site5Index >= 0 ? (values[site5Index]?.trim() || '') : '';

    const validationErrors: string[] = [];

    // Validate required fields
    if (!firstName) validationErrors.push('Prénom manquant');
    if (!lastName) validationErrors.push('Nom manquant');
    if (!role) validationErrors.push('Rôle manquant');

    // Validate role
    if (role && !isValidRole(role)) {
      validationErrors.push(`Rôle invalide: ${role}`);
    }

    // Generate username
    let username = '';
    if (firstName && lastName) {
      const baseUsername = generateUsername(lastName, firstName);

      // Handle duplicates by adding a numeric suffix
      const count = generatedUsernames.get(baseUsername) || 0;
      username = count === 0 ? baseUsername : `${baseUsername}${count + 1}`;
      generatedUsernames.set(baseUsername, count + 1);
    }

    // Collect site names (ignore sites for admin and director roles)
    const roleIgnoresSites = role === 'admin' || role === 'director';
    const siteNames: string[] = [];

    if (!roleIgnoresSites) {
      // Only collect sites for operator and manager roles
      [site1, site2, site3, site4, site5].forEach(site => {
        if (site && site.length > 0) {
          siteNames.push(site);
        }
      });

      // Check if role requires sites
      const requiresSite = role === 'operator' || role === 'manager';
      if (requiresSite && siteNames.length === 0) {
        validationErrors.push('Au moins un site requis pour ce rôle');
      }

      // Identify new sites
      siteNames.forEach(siteName => {
        if (!existingSiteNames.has(siteName.toLowerCase())) {
          allNewSites.add(siteName);
        }
      });
    }

    users.push({
      lineNumber,
      first_name: firstName,
      last_name: lastName,
      role: role,
      site1: roleIgnoresSites ? '' : site1,
      site2: roleIgnoresSites ? '' : site2,
      site3: roleIgnoresSites ? '' : site3,
      site4: roleIgnoresSites ? '' : site4,
      site5: roleIgnoresSites ? '' : site5,
      generatedUsername: username,
      validationErrors,
      isValid: validationErrors.length === 0,
    });
  }

  // Check for existing usernames in database
  const usernamesToCheck = users.map(u => u.generatedUsername).filter(Boolean);
  const existingUsernames = await checkUsernamesAvailability(usernamesToCheck);

  // Mark users with existing usernames as invalid
  users.forEach(user => {
    if (existingUsernames.has(user.generatedUsername)) {
      user.validationErrors.push('Nom d\'utilisateur déjà existant en base');
      user.isValid = false;
    }
  });

  return {
    users,
    newSites: Array.from(allNewSites),
    errors,
  };
}

// Parse a CSV line handling quoted values with specified separator
function parseCSVLine(line: string, separator: string = ','): string[] {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === separator && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }

  values.push(currentValue.trim());
  return values;
}

// Generate a sample CSV template with 5 site columns
export function generateCSVTemplate(): string {
  const headers = 'first_name,last_name,role,site1,site2,site3,site4,site5';
  const examples = [
    'Jean,Dupont,operator,Site A,Site B,,,',
    'Marie,Martin,manager,Site A,,,,',
    'Pierre,Durand,director,,,,,',
    'Sophie,Bernard,admin,,,,,',
  ];

  return headers + '\n' + examples.join('\n');
}

// Download template CSV file
export function downloadCSVTemplate(): void {
  const content = generateCSVTemplate();
  // Use createCSVBlob to add UTF-8 BOM for proper Excel compatibility
  const blob = createCSVBlob(content);
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', 'modele_import_utilisateurs.csv');
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Generate CSV with import results
export function generateResultsCSV(
  results: Array<{
    username: string;
    firstName: string;
    lastName: string;
    role: string;
    site1?: string;
    site2?: string;
    site3?: string;
    site4?: string;
    site5?: string;
    password?: string;
    error?: string;
  }>
): string {
  const headers = 'username,first_name,last_name,role,site1,site2,site3,site4,site5,password,status';
  const rows = results.map(r => {
    const status = r.error ? 'ERREUR' : 'SUCCÈS';
    const password = r.password || '';
    const errorInfo = r.error ? ` - ${r.error}` : '';

    const site1 = r.site1 || '';
    const site2 = r.site2 || '';
    const site3 = r.site3 || '';
    const site4 = r.site4 || '';
    const site5 = r.site5 || '';

    return `${r.username},${r.firstName},${r.lastName},${r.role},${site1},${site2},${site3},${site4},${site5},${password},${status}${errorInfo}`;
  });

  return headers + '\n' + rows.join('\n');
}

// Download results CSV
export function downloadResultsCSV(
  results: Array<{
    username: string;
    firstName: string;
    lastName: string;
    role: string;
    site1?: string;
    site2?: string;
    site3?: string;
    site4?: string;
    site5?: string;
    password?: string;
    error?: string;
  }>,
  filename: string = 'resultats_import_utilisateurs.csv'
): void {
  const content = generateResultsCSV(results);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
